import {
  isPrimaryColor,
  isPrimaryOrSecondaryColor,
  isSecondaryColor,
  getOppositePosition,
} from '../../utils';
import { mode, StyleFunctionProps } from '@chakra-ui/theme-tools';
import type { ComponentStyleConfig } from '@chakra-ui/theme';

const Tabs: ComponentStyleConfig = {
  parts: [
    'Tabs',
    'TabList',
    'Tab',
    'TabLabel',
    'TabPanels',
    'TabPanel',
    'AddButton',
    'ActionIcon',
  ],
  baseStyle: (props: StyleFunctionProps) => ({
    Tabs: { display: 'flex' },
    TabList: {
      overFlow: 'clip',
      borderColor: isPrimaryOrSecondaryColor(props.colorScheme)
        ? props.colorScheme
        : `${props.colorScheme}.600`,
      gap: 4,
      borderBottom: 0,
      [`border${getOppositePosition(props.tabPosition)}`]: '1px',
    },
    Tab: {
      whiteSpace: 'nowrap',
      maxW: 'xs',
      isTruncated: true,
      mb: '-px',
      fontSize: 'sm',
      fontWeight: 'medium',
      color: mode('gray.900', 'gray.400')(props),
      borderBottom: 0,
      _hover: {
        color: mode('gray.700', 'gray.200')(props),
        borderColor: mode('gray.500', 'gray.100'),
      },
      _focus: { boxShadow: 'none' },
      _selected: {
        borderColor: 'primary',
        [`margin${getOppositePosition(props.tabPosition)}`]: '-1px',
        [`border${getOppositePosition(props.tabPosition)}`]: '2px',
        [`border${getOppositePosition(props.tabPosition)}Color`]: mode(
          'white',
          'gray.800',
        )(props),
        color: isPrimaryOrSecondaryColor(props.colorScheme)
          ? props.colorScheme
          : `${props.colorScheme}.500`,
      },
    },
    TabLabel: {
      mb: 0,
      whiteSpace: 'nowrap',
      isTruncated: true,
      display: 'flex',
      alignItems: 'center',
    },
    TabPanels: {},
    TabPanel: {},
    AddButton: {
      borderRadius: 'none',
      borderWidth: 1,
      borderColor: props.colorScheme || 'primary',
      mb: '-px',
      color: mode('black', 'white')(props),
      _focus: { boxShadow: 'none' },
    },
    ActionIcon: { colorScheme: 'primary' },
  }),
  variants: {
    line: (props) => ({
      Tab: {
        _selected: {
          [`border${getOppositePosition(props.tabPosition)}Color`]: 'primary',
        },
      },
    }),
    enclosed: (props) => ({
      Tab: {
        _selected: {
          [`border${getOppositePosition(
            props.tabPosition,
          )}`]: `1px solid ${mode('white', 'black')(props)}`,
        },
        _hover: {
          [`border${getOppositePosition(
            props.tabPosition,
          )}`]: `1px solid ${mode('white', 'black')(props)}`,
        },
      },
    }),
    'enclosed-colored': (props) => ({
      Tab: {
        [`border${getOppositePosition(props.tabPosition)}`]: `1px solid`,
        [`border${getOppositePosition(props.tabPosition)}`]: mode(
          'primary',
          'white',
        )(props),
        _selected: {
          [`border${getOppositePosition(
            props.tabPosition,
          )}`]: `1px solid ${mode('white', 'black')(props)}`,
        },
      },
    }),
    'soft-rounded': (props) => ({
      TabList: {
        [`border${getOppositePosition(props.tabPosition)}Width`]: 0,
      },
      Tab: {
        borderRadius: 'base',
        _hover: {
          bg: isPrimaryOrSecondaryColor(props.colorScheme)
            ? props.colorScheme
            : `${props.colorScheme}.600`,
          color: isPrimaryColor(props.colorScheme)
            ? mode('white', 'gray.800')(props)
            : isSecondaryColor(props.colorScheme)
            ? mode('black', 'white')(props)
            : `${props.colorScheme}.200`,
        },
        _selected: {
          bg: isPrimaryOrSecondaryColor(props.colorScheme)
            ? props.colorScheme
            : `${props.colorScheme}.600`,
          color: isPrimaryColor(props.colorScheme)
            ? mode('white', 'gray.800')(props)
            : isSecondaryColor(props.colorScheme)
            ? mode('black', 'white')(props)
            : `${props.colorScheme}.200`,
        },
      },
    }),
    'solid-rounded': (props) => ({
      TabList: {
        [`border${getOppositePosition(props.tabPosition)}Width`]: 0,
      },
      Tab: {
        borderRadius: 'base',
        _hover: {
          bg: isPrimaryOrSecondaryColor(props.colorScheme)
            ? props.colorScheme
            : `${props.colorScheme}.800`,
          color: isPrimaryColor(props.colorScheme)
            ? mode('white', 'gray.800')(props)
            : isSecondaryColor(props.colorScheme)
            ? mode('black', 'white')(props)
            : `${props.colorScheme}.200`,
        },
        _selected: {
          bg: isPrimaryOrSecondaryColor(props.colorScheme)
            ? props.colorScheme
            : `${props.colorScheme}.800`,
          color: isPrimaryColor(props.colorScheme)
            ? mode('white', 'gray.800')(props)
            : isSecondaryColor(props.colorScheme)
            ? mode('black', 'white')(props)
            : `${props.colorScheme}.200`,
        },
      },
    }),
    unstyled: (props) => ({
      TabList: {
        [`border${getOppositePosition(props.tabPosition)}Width`]: 0,
      },
      Tab: {
        _selected: { color: 'unset' },
      },
    }),
  },
  defaultProps: { variant: 'enclosed', colorScheme: 'primary' },
};

export default Tabs;
